import { GoogleGenAI, Type } from "@google/genai";
import { MaintenanceItem, Priority } from '../../types';

/**
 * Uses the Gemini API to generate structured task details (title, description, priority)
 * from a natural language string provided by the user.
 * @param {string} text - The user's unstructured text about the maintenance issue.
 * @returns {Promise<{ title: string; description: string; priority: Priority }>} A promise that resolves to an object containing the structured task details.
 * @throws {Error} If the API call fails or the response cannot be parsed.
 */
export async function generateTaskDetailsFromText(text: string): Promise<{ title: string; description: string; priority: Priority }> {
    // Get API key from Vite environment variables
    const apiKey = import.meta.env.VITE_GEMINI_API_KEY || process.env.GEMINI_API_KEY;
    
    if (!apiKey || apiKey === 'undefined' || apiKey === 'your_gemini_api_key_here') {
        throw new Error('Gemini API key is not configured. Please set VITE_GEMINI_API_KEY in your .env file.');
    }

    try {
        // The API key is provided via an environment variable, as per security best practices.
        const ai = new GoogleGenAI({ apiKey });

        // The prompt provides context and instructions to the AI model.
        // It defines the role of the AI, the task to perform, and the expected output format.
        const prompt = `You are an intelligent assistant for a facility maintenance application. Analyze the following user request and extract a concise title, a detailed description, and a priority level. 
        The priority must be one of 'Critical', 'High', 'Medium', or 'Low'.
        - Use 'Critical' for emergencies like fire, major leaks, security breaches, or situations that pose an immediate threat to safety or property.
        - Use 'High' for urgent issues that impact residents' comfort or facility operations, like a broken AC unit, a clogged toilet in a common area, or a broken elevator.
        - Use 'Medium' for standard, non-urgent repairs like a dripping faucet, a broken light fixture, or a sticky door.
        - Use 'Low' for minor cosmetic issues or tasks that can be scheduled for a later time, like scuffed paint or a loose handrail.

        User request: "${text}"

        Respond with a JSON object that strictly follows the provided schema.`;
        
        const responseSchema = {
            type: Type.OBJECT,
            properties: {
                title: {
                    type: Type.STRING,
                    description: "A concise, descriptive title for the maintenance task. Should be around 5-10 words."
                },
                description: {
                    type: Type.STRING,
                    description: "A detailed description of the issue, taken directly from the user's request."
                },
                priority: {
                    type: Type.STRING,
                    description: "The suggested priority level.",
                    enum: [Priority.Critical, Priority.High, Priority.Medium, Priority.Low]
                }
            },
            required: ["title", "description", "priority"]
        };

        const response = await ai.models.generateContent({
            model: "gemini-2.5-flash",
            contents: prompt,
            config: {
                responseMimeType: "application/json",
                responseSchema: responseSchema,
            },
        });

        const jsonString = response.text?.trim() || '';
        const parsedResponse = JSON.parse(jsonString);

        // Validate that the priority received from the AI is a valid enum value.
        // This acts as a safeguard against the AI returning an unexpected string.
        if (!Object.values(Priority).includes(parsedResponse.priority)) {
            console.warn(`AI returned an invalid priority: ${parsedResponse.priority}. Defaulting to Medium.`);
            parsedResponse.priority = Priority.Medium;
        }

        return parsedResponse;

    } catch (error) {
        console.error("Error calling Gemini API:", error);
        throw new Error("Failed to generate task details with AI. Please fill in the details manually.");
    }
}

/**
 * Summarizes the description and message history of a maintenance task using the Gemini API.
 * @param {MaintenanceItem} item - The maintenance item, including its messages.
 * @returns {Promise<string>} A promise that resolves to a string containing the AI-generated summary.
 * @throws {Error} If the API call fails.
 */
export async function summarizeTaskActivity(item: MaintenanceItem): Promise<string> {
    // Get API key from Vite environment variables
    const apiKey = import.meta.env.VITE_GEMINI_API_KEY || process.env.GEMINI_API_KEY;
    
    if (!apiKey || apiKey === 'undefined' || apiKey === 'your_gemini_api_key_here') {
        throw new Error('Gemini API key is not configured. Please set VITE_GEMINI_API_KEY in your .env file.');
    }

    try {
        const ai = new GoogleGenAI({ apiKey });
        
        // Format the messages chronologically to provide a clear conversation history to the AI.
        const formattedMessages = (item.messages || [])
            .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
            .map(msg => `${msg.created_by_first_name} ${msg.created_by_last_name}: "${msg.message}"`)
            .join("\n");

        const prompt = `You are an expert maintenance log analyst. Your task is to provide a concise summary of a maintenance ticket based on its initial description and the subsequent conversation history.

The summary should be brief (2-4 sentences) and cover:
1. The core problem.
2. The key actions or discussions that have taken place.
3. The current unresolved status or next steps, if apparent from the conversation.

Do not invent information. Base your summary strictly on the text provided.

**Task Title:** ${item.title}
**Task Description:** ${item.description}

**Activity Log (oldest to newest):**
${formattedMessages.length > 0 ? formattedMessages : "No messages yet."}

**Summary:**`;

        const response = await ai.models.generateContent({
            model: "gemini-2.5-flash",
            contents: prompt,
        });
        
        return response.text?.trim() || 'Unable to generate summary.';
    } catch (error) {
        console.error("Error calling Gemini API for summarization:", error);
        throw new Error("Failed to generate a summary. The AI service may be temporarily unavailable.");
    }
}